/**
 * LocationDialogs
 * =====================================================================
 * Two location-management dialogs extracted from App.js:
 *
 *   - EditLocationDialog       — single-location edit form (~120 lines)
 *   - ManageLocationsDialog    — list + per-row actions (~266 lines)
 *
 * The "Add Location" dialog was intentionally LEFT inline in App.js
 * this pass because its body has ~115 lines of unrelated
 * "Create Treatment Plan from PANs" code wired to the notes-creation
 * state machine (newNoteFormData, selectedTemplate, handleCreateClinicalNote,
 * etc.).  Extracting it safely needs a separate pass.  P2 cleanup task.
 *
 * Parent retains state ownership — these components are dumb-view +
 * callback shells so the legacy state machine in App.js is untouched.
 *
 * Feb 2026 refactor.
 */

import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Users, ChevronDown, ChevronRight, Archive } from 'lucide-react';
import { useZipAutofill } from '../../hooks/useZipAutofill';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { DebouncedInput } from '../ui/debounced-textarea';

const LOCATION_TYPES = [
  ['main', 'Main Office'],
  ['satellite', 'Satellite Office'],
  ['remote', 'Remote/Telehealth'],
];

// ────────────────────────────────────────────────────────────────────
// EditLocationDialog
// ────────────────────────────────────────────────────────────────────
export const EditLocationDialog = ({
  open,
  onOpenChange,
  editingLocation,
  formData,
  setFormData,
  onSubmit,
  onCancel,
}) => {
  const patch = (k, v) => setFormData({ ...formData, [k]: v });
  const zipDigits = (formData.zip_code || '').replace(/[^0-9]/g, '');

  // June 2026 — ZIP → city/state autofill (MA dataset, empty-only).
  const zipAutofill = useZipAutofill({
    formData, setField: patch,
    zipField: 'zip_code', cityField: 'city', stateField: 'state',
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Location - {editingLocation?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Location Name</Label>
              <DebouncedInput
                value={formData.name}
                onChange={(e) => patch('name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Location Type</Label>
              <Select
                value={formData.location_type}
                onValueChange={(v) => patch('location_type', v)}
              >
                <SelectTrigger data-testid="edit-location-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_TYPES.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {/* Practice Address — used for the location's Service
              Facility on 837 claims AND as a fallback for the 270
              Information Receiver address.  Medicare CEDI requires
              9-digit ZIP+4 matching PECOS, so the ZIP input shows a
              live warning when fewer digits are entered. */}
          <div>
            <Label>Street Address</Label>
            <DebouncedInput
              value={formData.address_line1 || ''}
              onChange={(e) => patch('address_line1', e.target.value)}
              placeholder="100 Main St"
              data-testid="edit-location-address-line1"
            />
          </div>
          <div>
            <Label>Address Line 2 (Suite, Floor, etc.)</Label>
            <DebouncedInput
              value={formData.address_line2 || ''}
              onChange={(e) => patch('address_line2', e.target.value)}
              placeholder="Suite 412"
              data-testid="edit-location-address-line2"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>City</Label>
              <DebouncedInput
                value={formData.city || ''}
                onChange={(e) => patch('city', e.target.value)}
                placeholder="Worcester"
                data-testid="edit-location-city"
              />
            </div>
            <div>
              <Label>State</Label>
              <DebouncedInput
                value={formData.state || ''}
                onChange={(e) =>
                  patch('state', e.target.value.toUpperCase().slice(0, 2))
                }
                placeholder="MA"
                maxLength={2}
                data-testid="edit-location-state"
              />
            </div>
            <div>
              <Label>ZIP Code</Label>
              <DebouncedInput
                value={formData.zip_code || ''}
                onChange={(e) => zipAutofill.handleZipChange(e.target.value)}
                placeholder="01609-2422"
                data-testid="edit-location-zip"
              />
              {formData.zip_code && zipDigits.length < 9 && (
                <p
                  className="text-[11px] text-amber-700 mt-1"
                  data-testid="edit-location-zip-warning"
                >
                  ⚠ Medicare typically rejects with AAA*50 unless ZIP+4 is
                  provided.
                </p>
              )}
              {zipAutofill.autofillStatus === 'filled' && (
                <p className="text-[11px] text-emerald-600 mt-1" data-testid="edit-location-zip-autofilled">
                  Auto-filled city/state from ZIP.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Phone</Label>
              <DebouncedInput
                value={formData.phone}
                onChange={(e) => patch('phone', e.target.value)}
                data-testid="edit-location-phone"
              />
            </div>
            <div>
              <Label>Fax</Label>
              <DebouncedInput
                value={formData.fax}
                onChange={(e) => patch('fax', e.target.value)}
                data-testid="edit-location-fax"
              />
            </div>
          </div>
          <div>
            <Label>Service Facility NPI(s)</Label>
            <DebouncedInput
              value={formData.npis || ''}
              onChange={(e) => patch('npis', e.target.value)}
              placeholder="e.g., 1487863494 (comma-separated if multiple)"
              data-testid="edit-location-npis"
            />
            <p className="text-xs text-gray-500 mt-1">
              Used in claim Loop 2310C (Service Facility Location). Leave
              blank to bill under the organization&apos;s primary NPI.
            </p>
          </div>
          {/* Jun 2026 — EDI service-facility settings (Loop 2310C/2310D):
              per-location NM101 qualifier + optional secondary identifier
              so payers can differentiate locations sharing one Type 2 NPI. */}
          <div className="border rounded-md p-3 space-y-3 bg-slate-50/60">
            <p className="text-sm font-semibold text-gray-700">
              EDI Claim Settings — Service Facility Loop (2310C/2310D)
            </p>
            <div>
              <Label>Location Qualifier (NM101)</Label>
              <Select
                value={formData.edi_location_qualifier || '77'}
                onValueChange={(v) => patch('edi_location_qualifier', v)}
              >
                <SelectTrigger data-testid="edit-location-edi-qualifier">
                  <SelectValue placeholder="77 — Service Facility (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="77">77 — Service Facility (default)</SelectItem>
                  <SelectItem value="FA">FA — Facility</SelectItem>
                  <SelectItem value="LI">LI — Independent Lab</SelectItem>
                  <SelectItem value="TL">TL — Testing Laboratory</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Entity qualifier used on electronic claims when the service
                location differs from the billing address. Defaults to 77
                (Service Facility).
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Secondary ID Type (Loop 2310D REF)</Label>
                <Select
                  value={formData.edi_secondary_id_type || 'none'}
                  onValueChange={(v) => patch('edi_secondary_id_type', v === 'none' ? '' : v)}
                >
                  <SelectTrigger data-testid="edit-location-edi-secondary-type">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="taxonomy">Taxonomy Code (PXC)</SelectItem>
                    <SelectItem value="state_license">State License Number (0B)</SelectItem>
                    <SelectItem value="legacy_provider">Legacy Provider Number (G2)</SelectItem>
                    <SelectItem value="locational">Locational Identifier (LU)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Secondary ID Value</Label>
                <DebouncedInput
                  value={formData.edi_secondary_id_value || ''}
                  onChange={(e) => patch('edi_secondary_id_value', e.target.value)}
                  placeholder="e.g., 207Q00000X or LIC-12345"
                  data-testid="edit-location-edi-secondary-value"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              When set, a REF segment is appended to the service facility
              loop on outbound EDI claims so payers can differentiate
              locations operating under the same Type 2 NPI.
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="edit-location-save"
            >
              Update Location
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────────
// ManageLocationsDialog
// ────────────────────────────────────────────────────────────────────
export const ManageLocationsDialog = ({
  open,
  onOpenChange,
  apiBase,
  locations,
  setLocations,
  providers,
  locationProviderCounts = {},
  setEditingLocation,
  setLocationFormData,
  setLocationProviders,
  setShowLocationProviders,
  setShowEditLocation,
  setShowAddLocation,
  adminJumpDropdown = null,
}) => {
  // Archived locations collapse under an accordion (user request).
  const [showArchived, setShowArchived] = React.useState(false);

  const handleSetPrimary = async (location) => {
    try {
      await axios.put(`${apiBase}/locations/${location.id}/set-primary`);
      toast.success(`${location.name} set as primary location`);
    } catch (error) {
      console.error('Error setting primary location:', error);
      toast.error(
        error.response?.data?.detail || 'Failed to set primary location',
      );
    }
  };

  const handleViewProviders = async (location) => {
    setEditingLocation(location);
    try {
      const response = await axios.get(
        `${apiBase}/locations/${location.id}/providers`,
      );
      setLocationProviders(response.data);
    } catch (error) {
      console.error('Error fetching location providers:', error);
      setLocationProviders([]);
    }
    setShowLocationProviders(true);
  };

  const handleEdit = async (location) => {
    setEditingLocation(location);
    // Load every editable field from the canonical backend shape so
    // the dialog actually shows existing values.  This was the source
    // of the "ZIP won't save" bug — there was no ZIP field rendered,
    // and the address field was bound to a singular `address` key that
    // the backend doesn't have.
    setLocationFormData({
      name: location.name || '',
      location_type: location.location_type || location.type || '',
      address_line1: location.address_line1 || '',
      address_line2: location.address_line2 || '',
      city: location.city || '',
      state: location.state || '',
      zip_code: location.zip_code || '',
      phone: location.phone || '',
      fax: location.fax || '',
      edi_location_qualifier: location.edi_location_qualifier || '77',
      edi_secondary_id_type: location.edi_secondary_id_type || '',
      edi_secondary_id_value: location.edi_secondary_id_value || '',
    });
    try {
      const response = await axios.get(
        `${apiBase}/locations/${location.id}/providers`,
      );
      setLocationProviders(response.data);
    } catch (error) {
      console.error('Error fetching location providers:', error);
      setLocationProviders([]);
    }
    setShowEditLocation(true);
  };

  const handleArchive = async (location) => {
    // Archive (soft) — preserves every reference (claims, notes,
    // appointments) while hiding from active dropdowns.  Right
    // action when a location is being retired.
    if (
      !window.confirm(
        `Archive "${location.name}"?\n\n` +
          `It will be hidden from active selectors but every existing claim, note, and appointment that references it stays intact. ` +
          `You can restore it from the archived view if needed.`,
      )
    ) {
      return;
    }
    try {
      await axios.post(`${apiBase}/locations/${location.id}/archive`);
      // Reflect the archived state locally so the row shows it
      // immediately.  Was leaving the row visible-and-not-archived,
      // which looked like the action didn't take (user-reported,
      // Feb 2026).
      setLocations((prev) =>
        Array.isArray(prev)
          ? prev.map((l) =>
              l.id === location.id ? { ...l, is_active: false } : l,
            )
          : prev,
      );
      toast.success(`${location.name} archived`);
      // Belt-and-suspenders refetch so derived state (e.g.
      // is_primary auto-flip) shows up too.
      try {
        const fresh = await axios.get(`${apiBase}/locations`);
        if (Array.isArray(fresh.data)) setLocations(fresh.data);
      } catch (_) {
        /* non-fatal */
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive location');
    }
  };

  const handleRestore = async (location) => {
    try {
      await axios.post(`${apiBase}/locations/${location.id}/unarchive`);
      setLocations((prev) =>
        Array.isArray(prev)
          ? prev.map((l) =>
              l.id === location.id ? { ...l, is_active: true } : l,
            )
          : prev,
      );
      toast.success(`${location.name} restored to active`);
      try {
        const fresh = await axios.get(`${apiBase}/locations`);
        if (Array.isArray(fresh.data)) setLocations(fresh.data);
      } catch (_) {
        /* non-fatal */
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore location');
    }
  };

  const handleDelete = async (location) => {
    if (
      !window.confirm(
        `Delete "${location.name}" PERMANENTLY?\n\n` +
          `Use Delete only if this location was added by mistake and has never been used. ` +
          `For everyday retirement use Archive instead.`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${apiBase}/locations/${location.id}`);
      toast.success('Location deleted successfully!');
    } catch (error) {
      // Backend returns a structured 409 with counts when references
      // exist — surface those numbers so the biller knows the
      // archive button is what they want.
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'location_in_use') {
        const refs = detail.references || {};
        const partsArr = Object.entries(refs)
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${n} ${k.replace('_', ' ')}`);
        toast.error(
          `Cannot delete — ${partsArr.join(', ')}. Use Archive instead.`,
          { duration: 6000 },
        );
      } else {
        toast.error(detail || 'Failed to delete location');
      }
    }
  };

  const sortedLocations = [...locations].sort((a, b) =>
    (a.name || '').localeCompare(b.name || ''),
  );
  const activeLocations = sortedLocations.filter((l) => l.is_active !== false);
  const archivedLocations = sortedLocations.filter((l) => l.is_active === false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <DialogTitle>Manage Locations</DialogTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => setShowAddLocation(true)}
              className="bg-teal-600 hover:bg-teal-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Location
            </Button>
            {adminJumpDropdown}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {(() => {
            const renderLocationCard = (location) => (
            <Card
              key={location.id}
              className={`p-4 ${location.is_primary ? 'border-teal-500 border-2' : ''} ${location.is_active === false ? 'opacity-60 bg-gray-50' : ''}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div>
                    <h4 className="font-medium text-lg">{location.name}</h4>
                    <p className="text-sm text-gray-600 capitalize">
                      {location.type} Office
                    </p>
                  </div>
                  {location.is_primary && (
                    <Badge className="bg-teal-100 text-teal-700">Primary</Badge>
                  )}
                  {location.is_active === false && (
                    <Badge
                      className="bg-amber-100 text-amber-800 border-amber-300"
                      data-testid={`location-archived-badge-${location.id}`}
                    >
                      Archived
                    </Badge>
                  )}
                </div>
                <div className="flex space-x-2">
                  {!location.is_primary && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-teal-500 text-teal-600 hover:bg-teal-50"
                      onClick={() => handleSetPrimary(location)}
                      title="Set as primary location for PDF headers"
                    >
                      Set as Primary
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewProviders(location)}
                    title="View providers at this location"
                  >
                    <Users className="h-4 w-4 mr-1" />
                    View Providers ({locationProviderCounts[location.id] || 0})
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(location)}
                    title="Edit location"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {location.is_active === false ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => handleRestore(location)}
                      title="Restore — returns this location to active use"
                      data-testid={`restore-location-${location.id}`}
                    >
                      Restore
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                      onClick={() => handleArchive(location)}
                      title="Archive (soft) — hides from active use, preserves history"
                      data-testid={`archive-location-${location.id}`}
                    >
                      Archive
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(location)}
                    title="Delete permanently — only for locations added by mistake"
                    data-testid={`delete-location-${location.id}`}
                  >
                    Delete
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div>
                  <Label className="text-xs text-gray-500">Address</Label>
                  <p>
                    {location.address_line1 && (
                      <>
                        {location.address_line1}
                        <br />
                      </>
                    )}
                    {location.address_line2 && (
                      <>
                        {location.address_line2}
                        <br />
                      </>
                    )}
                    {(location.city || location.state || location.zip_code) && (
                      <>
                        {location.city}
                        {location.city && location.state ? ', ' : ''}
                        {location.state} {location.zip_code}
                      </>
                    )}
                    {!location.address_line1 &&
                      !location.city &&
                      'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone / Fax</Label>
                  <p>
                    {location.phone} / {location.fax}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">License</Label>
                  <p>
                    {location.license} (Exp:{' '}
                    {location.license_expiry || 'Not set'})
                  </p>
                </div>
              </div>

              {(location.assigned_providers || []).length > 0 && (
                <div className="mt-3">
                  <Label className="text-xs text-gray-500">
                    Assigned Providers
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(location.assigned_providers || [])
                      .slice(0, 3)
                      .map((providerId) => {
                        const provider = providers?.find(
                          (p) => p.id === providerId,
                        );
                        return provider ? (
                          <Badge
                            key={providerId}
                            variant="outline"
                            className="text-xs"
                          >
                            {provider.full_name}
                          </Badge>
                        ) : null;
                      })}
                    {location.assigned_providers.length > 3 && (
                      <Badge variant="outline" className="text-xs">
                        +{(location.assigned_providers || []).length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </Card>
            );
            return (
              <>
                {activeLocations.map((location) => renderLocationCard(location))}
                {archivedLocations.length > 0 && (
                  <div className="border rounded-lg" data-testid="archived-locations-section">
                    <button
                      type="button"
                      onClick={() => setShowArchived((v) => !v)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      aria-expanded={showArchived}
                      data-testid="archived-locations-toggle"
                    >
                      <span className="flex items-center gap-2">
                        {showArchived ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        <Archive className="h-4 w-4 text-amber-600" />
                        Archived Locations
                        <Badge variant="outline" className="text-xs">
                          {archivedLocations.length}
                        </Badge>
                      </span>
                      <span className="text-xs text-gray-400 font-normal">
                        {showArchived ? 'Hide' : 'Show'}
                      </span>
                    </button>
                    {showArchived && (
                      <div className="px-3 pb-3 pt-1 space-y-4 border-t bg-gray-50/60" data-testid="archived-locations-list">
                        {archivedLocations.map((location) => renderLocationCard(location))}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}

          {locations.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No locations configured</p>
              <Button
                onClick={() => setShowAddLocation(true)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Your First Location
              </Button>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
